/** Default blink window when no estimate is set (ms). */
export const URGENT_NEAR_MS = 15 * 60 * 1000;

export const WORKFLOW_URGENT_REACTION_ID = 'wf:urgency:urgent';

export const parseUrgentDeadline = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? value : null;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isFinite(date.getTime()) ? date : null;
};

export const isOpenUrgentTask = (task) => {
  if (!task || !parseUrgentDeadline(task.urgentDeadlineAt)) return false;
  const status = task.status || task.statusString;
  return status !== 'COMPLETED' && status !== 'CANCELLED';
};

export const isOpenUrgentWorkflow = (item) => {
  if (!item) return false;
  const byCategory = item.workflowByCategory || {};
  const isUrgent = byCategory.urgency === WORKFLOW_URGENT_REACTION_ID
    || Object.values(byCategory).includes(WORKFLOW_URGENT_REACTION_ID);
  if (!isUrgent) return false;
  return byCategory['workflow-status'] !== 'wf:workflow-status:closed';
};

/** Sort comparator: urgent items first (soonest deadline first), then 0 for tie. */
export const compareUrgentFirst = (a, b, {
  isUrgent = isOpenUrgentTask,
  getDeadline = (item) => item?.urgentDeadlineAt,
} = {}) => {
  const aUrgent = Boolean(isUrgent(a));
  const bUrgent = Boolean(isUrgent(b));
  if (aUrgent !== bUrgent) return aUrgent ? -1 : 1;
  if (!aUrgent) return 0;
  const aMs = parseUrgentDeadline(getDeadline(a))?.getTime() ?? Number.POSITIVE_INFINITY;
  const bMs = parseUrgentDeadline(getDeadline(b))?.getTime() ?? Number.POSITIVE_INFINITY;
  return aMs - bMs;
};

export const toLocalDateTimeString = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!Number.isFinite(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const toDatetimeLocalValue = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const getUrgentRemainingMs = (deadlineAt, now = Date.now()) => {
  const deadline = parseUrgentDeadline(deadlineAt);
  if (!deadline) return null;
  return deadline.getTime() - now;
};

/**
 * Blink when remaining time is within the allocated estimate.
 * Example: estimate 30 min, deadline in 2h → no blink until ≤30 min left.
 * Falls back to 15 min when estimate is missing.
 */
export const getUrgentBlinkThresholdMs = (estimateMinutes) => {
  const mins = Number(estimateMinutes);
  if (Number.isFinite(mins) && mins > 0) {
    return Math.round(mins * 60 * 1000);
  }
  return URGENT_NEAR_MS;
};

export const shouldBlinkUrgent = (deadlineAt, estimateMinutes, now = Date.now()) => {
  const remaining = getUrgentRemainingMs(deadlineAt, now);
  if (remaining == null) return false;
  if (remaining <= 0) return true;
  return remaining <= getUrgentBlinkThresholdMs(estimateMinutes);
};

/** @deprecated Prefer shouldBlinkUrgent with estimateMinutes */
export const isUrgentDeadlineNearOrOverdue = (deadlineAt, now = Date.now(), estimateMinutes) =>
  shouldBlinkUrgent(deadlineAt, estimateMinutes, now);

export const formatUrgentCountdown = (deadlineAt, now = Date.now()) => {
  const remaining = getUrgentRemainingMs(deadlineAt, now);
  if (remaining == null) return null;
  const overdue = remaining <= 0;
  const abs = Math.abs(remaining);
  const totalSec = Math.floor(abs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  let body;
  if (days > 0) {
    body = `${days}d ${hours}h`;
  } else if (hours > 0) {
    body = `${hours}h ${String(minutes).padStart(2, '0')}m`;
  } else if (minutes > 0) {
    body = `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  } else {
    body = `${seconds}s`;
  }
  return overdue ? `Overdue ${body}` : `${body} left`;
};

/** Solid red urgent row; add blink class only when remaining ≤ estimate. */
export const urgentListRowClass = (blink) =>
  `w-full min-w-0 !bg-red-100 dark:!bg-red-950/45 hover:!bg-red-200/80 dark:hover:!bg-red-900/50 border-l-4 border-l-red-500 dark:border-l-red-400 transition-colors${
    blink ? ' wl-urgent-blink' : ''
  }`;
